import { GenericOrderFoodAdditionalEntity } from "../../Objects/Order/GenericOrderFoodAdditionalEntity"
import { ICartItem } from "../../Interfaces/ICardItem"
import { IProductRequest } from "../../Interfaces/IProductRequest"
import { addtionalMemory, categoryMemory, comboMemory, companyMemory, drinksMemory, mealsMemory } from "../../Objects/Memory/Memory"
import { GenericFoodEntity } from "../../Objects/MealsAndDrinks/GenericFoodEntity"
import { CustomerEntity } from "../../Objects/Customer/CustomerEntity"
import { CalculatePromotions } from "../../Objects/Calculates/CalculatePromotions"
import { EComboItemType } from "../../Enum/EComboItemType"
import { ISelectedSelectionRequest } from "../../Interfaces/ISelectionRequest"

const FALLBACK_CATEGORY_ICON = "🍽️"

export const PUBLIC_MENU_COMBO_CATEGORY_ID = -9900

export const PUBLIC_MENU_RODIZIO_CATEGORY_ID = -9901

const COMBO_CATEGORY_NAME = "combos"

const getCurrentProductPrice = (food: GenericFoodEntity, customer?: CustomerEntity) => {
  const baseValue = Number(food.valueBase)

  if (!customer || !food.promotions || food.promotions.length === 0) {
    return {
      price: baseValue,
      oldPrice: undefined,
    }
  }

  const calculatePromotion = new CalculatePromotions({
    customer,
    mealsAndDrinks: food,
    addtionals: [],
    timeZoneId: companyMemory.timeZoneId,
  })

  const finalValue = Number(calculatePromotion.valueFinal)

  return {
    price: finalValue,
    oldPrice: finalValue < baseValue ? baseValue : undefined,
  }
}

export function buildAllProducts(customer?: CustomerEntity): IProductRequest[] {
  const comboCategory = categoryMemory.listData.find(
    (category) => category.name.trim().toLowerCase() === COMBO_CATEGORY_NAME,
  )

  const addtionals = addtionalMemory.listData.map((add) => ({
    id: add.id,
    name: add.name,
    image: add.iconUrl,
    price: Number(add.value),
    quantity: 1,
  }))

  const meals: IProductRequest[] = mealsMemory.listData.map((meal) => {
    const food: GenericFoodEntity = meal.genericFoodEntity

    const currentPrice = getCurrentProductPrice(food, customer)

    const freeAllowedIds = food.freeAllowedAddtionals?.map((e) => e.id) ?? []

    const availableAddOns = addtionals
      .filter((e) => food.allowedAddtionals.map((e) => e.id).includes(e.id))
      .map((e) => {
        const productAdditional = food.allowedAddtionals.find((a) => a.id === e.id)

        return {
          ...e,
          isFreeEligible: freeAllowedIds.includes(e.id),
          hasStockControl: productAdditional?.hasStockControl ?? false,
          stockQuantity: productAdditional?.stockQuantity ?? 0,
          isOutOfStock: productAdditional?.isOutOfStock ?? false,
        }
      })

    return {
      id: `meal-${meal.id}`,
      name: food.name,
      description: food.description,
      price: currentPrice.price,
      categoryId: food.categoryId,
      image: food.iconUrl,
      images: food.imageUrls,
      oldPrice: currentPrice.oldPrice,
      categoryIcon: food.category?.icon ?? FALLBACK_CATEGORY_ICON,
      badges: food.tags,
      hasPreparationTime: food.hasPreparationTime,
      preparationTimeMinutes: food.preparationTimeMinutes,
      availableAddOns: availableAddOns,
      freeAddtionalsLimit: food.freeAddtionalsLimit,
      productOrderMode: food.productOrderMode,
      reservationAllowedDays: food.reservationAllowedDays,
      reservationAllowedTimes: food.reservationAllowedTimes,
      isRodizioOnly: food.isRodizioOnly,
      availableSelections: (food.allowedSelections ?? []).map((selection) => ({
        id: selection.id,
        name: selection.name,
        options: selection.options.map((option) => ({
          id: option.id,
          name: option.name,
          image: option.iconUrl,
          value: option.value,
          isFree: option.isFree,
          allowMultiple: option.allowMultiple,
          maximumSelectionsLimit: option.maximumSelectionsLimit,
          isOutOfStock: option.isOutOfStock,
          hasStockControl: option.hasStockControl,
          stockQuantity: option.stockQuantity,
        })),
        allowMultiple: selection.allowMultiple,
        isRequired: selection.isRequired,
        requiredOptionsCount: selection.requiredOptionsCount,
        freeOptionsLimit: selection.freeOptionsLimit,
        hasMaximumSelectionsLimit: selection.hasMaximumSelectionsLimit,
        maximumSelectionsLimit: selection.maximumSelectionsLimit,
      })),
    }
  })

  const drinks = drinksMemory.listData.map((drink) => {
    const food = drink.genericFoodEntity

    const currentPrice = getCurrentProductPrice(food, customer)

    const freeAllowedIds = food.freeAllowedAddtionals?.map((e) => e.id) ?? []

    const availableAddOns = addtionals
      .filter((e) => food.allowedAddtionals.map((e) => e.id).includes(e.id))
      .map((e) => {
        const productAdditional = food.allowedAddtionals.find((a) => a.id === e.id)

        return {
          ...e,
          isFreeEligible: freeAllowedIds.includes(e.id),
          hasStockControl: productAdditional?.hasStockControl ?? false,
          stockQuantity: productAdditional?.stockQuantity ?? 0,
          isOutOfStock: productAdditional?.isOutOfStock ?? false,
        }
      })

    return {
      id: `drink-${drink.id}`,
      name: food.name,
      description: food.description,
      price: currentPrice.price,
      categoryId: food.categoryId,
      image: food.iconUrl,
      images: food.imageUrls,
      oldPrice: currentPrice.oldPrice,
      categoryIcon: food.category?.icon ?? FALLBACK_CATEGORY_ICON,
      badges: food.tags,
      hasPreparationTime: food.hasPreparationTime,
      preparationTimeMinutes: food.preparationTimeMinutes,
      availableAddOns: availableAddOns,
      freeAddtionalsLimit: food.freeAddtionalsLimit,
      productOrderMode: food.productOrderMode,
      reservationAllowedDays: food.reservationAllowedDays,
      reservationAllowedTimes: food.reservationAllowedTimes,
      isRodizioOnly: food.isRodizioOnly,
      availableSelections: (food.allowedSelections ?? []).map((selection) => ({
        id: selection.id,
        name: selection.name,
        options: selection.options.map((option) => ({
          id: option.id,
          name: option.name,
          image: option.iconUrl,
          value: option.value,
          isFree: option.isFree,
          allowMultiple: option.allowMultiple,
          maximumSelectionsLimit: option.maximumSelectionsLimit,
          isOutOfStock: option.isOutOfStock,
          hasStockControl: option.hasStockControl,
          stockQuantity: option.stockQuantity,
        })),
        allowMultiple: selection.allowMultiple,
        isRequired: selection.isRequired,
        requiredOptionsCount: selection.requiredOptionsCount,
        freeOptionsLimit: selection.freeOptionsLimit,
        hasMaximumSelectionsLimit: selection.hasMaximumSelectionsLimit,
        maximumSelectionsLimit: selection.maximumSelectionsLimit,
      })),
    }
  })

  const combos = comboMemory.listData.map((combo) => {
    const food = combo.genericFoodEntity

    const currentPrice = getCurrentProductPrice(food, customer)

    const freeAllowedIds = food.freeAllowedAddtionals?.map((e) => e.id) ?? []

    const availableAddOns = addtionals
      .filter((e) => food.allowedAddtionals.map((e) => e.id).includes(e.id))
      .map((e) => {
        const productAdditional = food.allowedAddtionals.find((a) => a.id === e.id)

        return {
          ...e,
          isFreeEligible: freeAllowedIds.includes(e.id),
          hasStockControl: productAdditional?.hasStockControl ?? false,
          stockQuantity: productAdditional?.stockQuantity ?? 0,
          isOutOfStock: productAdditional?.isOutOfStock ?? false,
        }
      })

    const comboItemsPreview = combo.items.map((item) => {
      const sourceName =
        item.itemType === EComboItemType.Meal
          ? mealsMemory.findById(item.itemId)?.genericFoodEntity?.name
          : drinksMemory.findById(item.itemId)?.genericFoodEntity?.name

      const prefix = item.itemType === EComboItemType.Meal ? "🍽️" : "🥤"
      const quantityLabel = item.quantity > 1 ? ` x${item.quantity}` : ""

      return `${prefix} ${sourceName ?? `Item #${item.itemId}`}${quantityLabel}`
    })

    return {
      id: `combo-${combo.id}`,
      linkedMealId: combo.mealId,
      name: food.name,
      description: food.description,
      price: currentPrice.price,
      categoryId: comboCategory?.id ?? PUBLIC_MENU_COMBO_CATEGORY_ID,
      image: food.iconUrl,
      images: food.imageUrls,
      oldPrice: currentPrice.oldPrice,
      categoryIcon: food.category?.icon ?? FALLBACK_CATEGORY_ICON,
      badges: ["Combo", ...(food.tags ?? [])],
      hasPreparationTime: food.hasPreparationTime,
      preparationTimeMinutes: food.preparationTimeMinutes,
      availableAddOns: availableAddOns,
      freeAddtionalsLimit: food.freeAddtionalsLimit,
      productOrderMode: food.productOrderMode,
      reservationAllowedDays: food.reservationAllowedDays,
      reservationAllowedTimes: food.reservationAllowedTimes,
      isRodizioOnly: food.isRodizioOnly,
      availableSelections: (food.allowedSelections ?? []).map((selection) => ({
        id: selection.id,
        name: selection.name,
        options: selection.options.map((option) => ({
          id: option.id,
          name: option.name,
          image: option.iconUrl,
          value: option.value,
          isFree: option.isFree,
          allowMultiple: option.allowMultiple,
          maximumSelectionsLimit: option.maximumSelectionsLimit,
          isOutOfStock: option.isOutOfStock,
          hasStockControl: option.hasStockControl,
          stockQuantity: option.stockQuantity,
        })),
        allowMultiple: selection.allowMultiple,
        isRequired: selection.isRequired,
        requiredOptionsCount: selection.requiredOptionsCount,
        freeOptionsLimit: selection.freeOptionsLimit,
        hasMaximumSelectionsLimit: selection.hasMaximumSelectionsLimit,
        maximumSelectionsLimit: selection.maximumSelectionsLimit,
      })),
      comboItemsPreview,
    }
  })

  return [...combos, ...meals, ...drinks]
}

export function mapSelections(item: { selectedSelections: ISelectedSelectionRequest[] }) {
  return item.selectedSelections.map((selection) => ({
    selectionEntityId: selection.selectionId,
    selectionOptionEntityId: selection.optionId,
  }))
}

export function mapAddtionals(item: ICartItem): GenericOrderFoodAdditionalEntity[] {
  return item.selectedAddOns.map((add) => {
    return new GenericOrderFoodAdditionalEntity({
      addtionalEntityId: add.id,
      amount: add.quantity,
    })
  })
}
